import { useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import clsx from 'clsx';
import { t } from '../lib/i18n.js';

// One Audio is allowed at a time — switching personas (or play→play on the
// same card) cancels the previous clip cleanly.
let activeAudio = null;
const subscribers = new Set();
function setActive(a) {
  if (activeAudio && activeAudio !== a) {
    try { activeAudio.pause(); } catch {}
    activeAudio.currentTime = 0;
  }
  activeAudio = a;
  subscribers.forEach((fn) => fn(a));
}
function onActiveChange(fn) {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
}

// Click counter lives in component state — each card owns its own index, so
// clicking Jose can't influence Collin or Allen, ever. First click plays
// clip 1, each subsequent click cycles to the next of N. Refresh resets to
// clip 1 (intentional — a returning visitor gets the canonical greeting).

// Image-forward persona tile. Gradient + icon render immediately; real
// photography at /personas/{id}.jpg overlays on top when the file exists.
// Drop photos into app/public/personas/ and the tiles upgrade themselves.
//
// Play-button: overlays in the top-left, plays a short ElevenLabs intro.
// Each click cycles to the next of 10 clips. Click does NOT select the
// persona (stopPropagation) — only the card body does.
export default function PersonaCard({ persona, selected, onSelect, lang = 'en', tagline: taglineOverride, blurb: blurbOverride, cta: ctaOverride }) {
  const Icon = persona.icon;
  // Explicit text props (the VitaMD pair cards pass these) win over the men's-health
  // i18n persona keys — pairs supply their own bilingual copy and must not fall through
  // to a colliding key like persona_executive_* (Collin).
  const tagline = taglineOverride ?? (t(lang, `persona_${persona.id}_tagline`) || persona.tagline);
  const blurb   = blurbOverride   ?? (t(lang, `persona_${persona.id}_blurb`)   || persona.blurb);
  const cta     = ctaOverride     ?? (t(lang, `persona_${persona.id}_cta`)     || persona.cta);
  const [imgOk, setImgOk] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [clipIdx, setClipIdx] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    return onActiveChange((a) => {
      if (a !== audioRef.current) setPlaying(false);
    });
  }, []);

  const togglePlay = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const clips = persona.audioClips || [];
    if (clips.length === 0) return;

    // Pause if this card is the one currently playing.
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
      if (activeAudio === audioRef.current) setActive(null);
      return;
    }

    const idx = clipIdx % clips.length;
    setClipIdx(idx + 1);
    const url = clips[idx];
    const a = new Audio(url);
    a.preload = 'auto';
    a.addEventListener('ended', () => {
      setPlaying(false);
      if (activeAudio === a) setActive(null);
    });
    a.addEventListener('error', () => setPlaying(false));
    audioRef.current = a;
    setActive(a);
    setPlaying(true);
    a.play().catch(() => setPlaying(false));
  };

  return (
    <button
      type="button"
      onClick={() => onSelect(persona)}
      className={clsx(
        'group relative aspect-[4/5] w-full overflow-hidden rounded-2xl border',
        'bg-gradient-to-br text-left transition-all duration-200',
        persona.accent,
        selected
          ? `ring-2 ${persona.ring || 'ring-alfred-gold'} border-alfred-gold shadow-xl shadow-alfred-gold/20`
          : 'border-stone-800 hover:border-alfred-gold/60 hover:shadow-lg hover:shadow-alfred-gold/10'
      )}
      data-persona-id={persona.id}
      aria-pressed={selected}
      aria-label={`Choose ${persona.name} — ${tagline}`}
    >
      {imgOk && persona.image && (
        <img
          src={persona.image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-70 transition-opacity group-hover:opacity-80"
          onError={() => setImgOk(false)}
        />
      )}

      {/* Vignette for text legibility even over busy photography */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/70 to-transparent" />

      {/* Play / pause — top-left, separate from card-select */}
      {persona.audioClips && persona.audioClips.length > 0 && (
        <span
          role="button"
          tabIndex={0}
          onClick={togglePlay}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') togglePlay(e);
          }}
          aria-label={playing ? `Pause ${persona.name}` : `Hear ${persona.name} introduce themselves`}
          className={clsx(
            'absolute left-4 top-4 z-20 inline-flex h-11 w-11 items-center justify-center rounded-full',
            'border border-alfred-gold/40 bg-stone-950/70 backdrop-blur-sm',
            'text-alfred-gold shadow-lg shadow-black/40',
            'transition hover:bg-alfred-gold hover:text-stone-950 hover:scale-105',
            playing && 'bg-alfred-gold text-stone-950 animate-pulse'
          )}
        >
          {playing ? <Pause size={18} strokeWidth={2} /> : <Play size={18} strokeWidth={2} className="ml-0.5" />}
        </span>
      )}

      {Icon && (
        <Icon
          className="absolute right-5 top-5 h-9 w-9 text-alfred-gold/80 drop-shadow"
          aria-hidden="true"
          strokeWidth={1.25}
        />
      )}

      <div className="relative z-10 flex h-full flex-col justify-end p-6">
        <p className="text-[11px] uppercase tracking-[0.25em] text-alfred-gold/90">
          {tagline}
        </p>
        <h3 className="mt-2 text-3xl font-serif text-stone-50">{persona.name}</h3>
        <p className="mt-2 text-sm text-stone-300 leading-relaxed">{blurb}</p>
        <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-alfred-gold">
          <span>{cta}</span>
          <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">→</span>
        </div>
      </div>
    </button>
  );
}
